import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '../context/ToastContext';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Chip,
  IconButton,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Grid,
  Autocomplete,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  IconPlus,
  IconTrash,
  IconEdit,
  IconFilter,
  IconPrinter,
  IconCash,
  IconTrendingUp,
  IconPackage,
} from '@tabler/icons-react';
import { motion } from 'framer-motion';
import {
  fetchMiscellaneousTypes,
  createMiscellaneousType,
  fetchMiscellaneous,
  createMiscellaneous,
  updateMiscellaneous,
  deleteMiscellaneous,
  getMiscellaneousSummary,
  createMiscellaneousPayment,
} from '../slices/miscellaneousSlice';
import { fetchStudents } from '../slices/studentsSlice';
import { fetchClasses } from '../slices/classesSlice';

const MotionPaper = motion(Paper);
const FONT = 'Cairo, Tajawal, sans-serif';

const MiscellaneousPage = () => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  
  // Redux state
  const {
    types = [],
    items = [],
    summary,
    loading,
    saving,
  } = useSelector((state) => state.miscellaneous || {});
  
  const students = useSelector((state) => state.students?.students || []);
  const { list: classes = [] } = useSelector((state) => state.classes || {});
  
  // Dialog states
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Form states
  const [itemData, setItemData] = useState({
    name: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    type: '',
    student: '',
    class_single: '',
    classes: [],
    notes: '',
  });
  
  const [typeData, setTypeData] = useState({
    name: '',
    description: '',
  });
  
  const [paymentData, setPaymentData] = useState({
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'cash',
    cheque_number: '',
    cheque_date: '',
    notes: '',
  });
  
  // Filter states
  const [dateFilter, setDateFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [studentFilter, setStudentFilter] = useState('all');
  const [classFilter, setClassFilter] = useState('all');
  const [filteredItems, setFilteredItems] = useState([]);
  
  useEffect(() => {
    dispatch(fetchMiscellaneousTypes());
    dispatch(fetchMiscellaneous());
    dispatch(getMiscellaneousSummary());
    dispatch(fetchStudents({ is_archived: false }));
    dispatch(fetchClasses());
  }, [dispatch]);
  
  // Filter items
  useEffect(() => {
    if (!items || items.length === 0) {
      setFilteredItems([]);
      return;
    }
    
    let filtered = [...items];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Date filter
    if (dateFilter === 'today') {
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.date);
        itemDate.setHours(0, 0, 0, 0);
        return itemDate.getTime() === today.getTime();
      });
    } else if (dateFilter === 'week') {
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.date);
        itemDate.setHours(0, 0, 0, 0);
        return itemDate >= weekAgo && itemDate <= today;
      });
    } else if (dateFilter === 'custom') {
      if (dateFrom) {
        const fromDate = new Date(dateFrom);
        fromDate.setHours(0, 0, 0, 0);
        filtered = filtered.filter(item => {
          const itemDate = new Date(item.date);
          itemDate.setHours(0, 0, 0, 0);
          return itemDate >= fromDate;
        });
      }
      if (dateTo) {
        const toDate = new Date(dateTo);
        toDate.setHours(23, 59, 59, 999);
        filtered = filtered.filter(item => {
          const itemDate = new Date(item.date);
          return itemDate <= toDate;
        });
      }
    }
    
    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(item => item.type === typeFilter);
    }
    
    // Student filter
    if (studentFilter !== 'all') {
      filtered = filtered.filter(item => item.student === studentFilter);
    }
    
    // Class filter
    if (classFilter !== 'all') {
      filtered = filtered.filter(item => 
        item.class_single === classFilter || 
        (item.classes_names && item.classes_names.includes(
          classes.find(c => c.id === classFilter)?.name
        ))
      );
    }
    
    setFilteredItems(filtered);
  }, [items, dateFilter, dateFrom, dateTo, typeFilter, studentFilter, classFilter, classes]);
  
  const handleSaveItem = async () => {
    try {
      if (!itemData.name.trim()) {
        showToast('❌ يرجى إدخال اسم العنصر');
        return;
      }
      if (!itemData.amount || parseFloat(itemData.amount) <= 0) {
        showToast('❌ يرجى إدخال مبلغ صحيح');
        return;
      }
      
      const payload = {
        name: itemData.name,
        amount: parseFloat(itemData.amount),
        date: itemData.date,
        type: itemData.type || null,
        student: itemData.student || null,
        class_single: itemData.class_single || null,
        classes: itemData.classes,
        notes: itemData.notes || null,
      };
      
      if (selectedItem) {
        await dispatch(updateMiscellaneous({ id: selectedItem.id, ...payload })).unwrap();
        showToast('✅ تم تحديث المتفرقات بنجاح');
      } else {
        await dispatch(createMiscellaneous(payload)).unwrap();
        showToast('✅ تم إضافة المتفرقات بنجاح');
      }
      
      setItemDialogOpen(false);
      setSelectedItem(null);
      setItemData({
        name: '',
        amount: '',
        date: new Date().toISOString().split('T')[0],
        type: '',
        student: '',
        class_single: '',
        classes: [],
        notes: '',
      });
      dispatch(fetchMiscellaneous());
      dispatch(getMiscellaneousSummary());
    } catch (error) {
      showToast(error?.payload?.error || '❌ فشل في حفظ المتفرقات');
    }
  };
  
  const handleDeleteItem = async (item) => {
    if (!window.confirm(`هل أنت متأكد من حذف: ${item.name}؟`)) {
      return;
    }
    
    try {
      await dispatch(deleteMiscellaneous(item.id)).unwrap();
      showToast('✅ تم حذف المتفرقات بنجاح');
      dispatch(fetchMiscellaneous());
      dispatch(getMiscellaneousSummary());
    } catch (error) {
      showToast('❌ فشل في حذف المتفرقات');
    }
  };
  
  const handleSaveType = async () => {
    try {
      if (!typeData.name.trim()) {
        showToast('❌ يرجى إدخال اسم النوع');
        return;
      }
      
      await dispatch(createMiscellaneousType({
        name: typeData.name,
        description: typeData.description || null,
      })).unwrap();
      
      showToast('✅ تم إضافة النوع بنجاح');
      setTypeDialogOpen(false);
      setTypeData({ name: '', description: '' });
      dispatch(fetchMiscellaneousTypes());
    } catch (error) {
      showToast(error?.payload?.error || '❌ فشل في إضافة النوع');
    }
  };
  
  const handleSavePayment = async () => {
    try {
      if (!selectedItem) return;
      if (!paymentData.amount || parseFloat(paymentData.amount) <= 0) {
        showToast('❌ يرجى إدخال مبلغ صحيح');
        return;
      }
      
      await dispatch(createMiscellaneousPayment({
        miscellaneous: selectedItem.id,
        amount: parseFloat(paymentData.amount),
        payment_date: paymentData.payment_date,
        payment_method: paymentData.payment_method,
        cheque_number: paymentData.cheque_number || null,
        cheque_date: paymentData.cheque_date || null,
        notes: paymentData.notes || null,
      })).unwrap();
      
      showToast('✅ تم تسجيل الدفعة بنجاح');
      setPaymentDialogOpen(false);
      setSelectedItem(null);
      setPaymentData({
        amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'cash',
        cheque_number: '',
        cheque_date: '',
        notes: '',
      });
      dispatch(fetchMiscellaneous());
      dispatch(getMiscellaneousSummary());
    } catch (error) {
      showToast(error?.payload?.error || '❌ فشل في تسجيل الدفعة');
    }
  };
  
  const handlePrint = () => {
    const itemsToPrint = dateFilter === 'all' ? items : filteredItems;
    
    if (itemsToPrint.length === 0) {
      showToast('❌ لا توجد بيانات للطباعة');
      return;
    }
    
    const printWindow = window.open('', '_blank');
    
    let filterText = '';
    if (dateFilter === 'today') {
      filterText = 'اليوم';
    } else if (dateFilter === 'week') {
      filterText = 'آخر أسبوع';
    } else if (dateFilter === 'custom') {
      filterText = `من ${dateFrom} إلى ${dateTo}`;
    } else {
      filterText = 'الكل';
    }
    
    const totalAmount = itemsToPrint.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
    const totalPaid = itemsToPrint.reduce((sum, item) => sum + (Number(item.total_paid) || 0), 0);
    const totalRemaining = totalAmount - totalPaid;
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html dir="rtl">
        <head>
          <title>تقرير المتفرقات</title>
          <meta charset="UTF-8">
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Alexandria:wght@400;500;600;700&display=swap');
            body {
              font-family: 'Alexandria', Arial, sans-serif;
              direction: rtl;
              padding: 20px;
            }
            h1 {
              text-align: center;
              color: #4d4cac;
            }
            .filter-info {
              text-align: center;
              margin-bottom: 20px;
              color: #666;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: right;
            }
            th {
              background-color: #4d4cac;
              color: white;
              font-weight: bold;
            }
            tr:nth-child(even) {
              background-color: #f2f2f2;
            }
            .summary {
              margin-top: 20px;
              padding: 15px;
              background-color: #f5f5f5;
              border-radius: 5px;
            }
            .summary-row {
              display: flex;
              justify-content: space-between;
              margin: 10px 0;
              font-size: 16px;
            }
            .summary-label {
              font-weight: bold;
            }
          </style>
        </head>
        <body>
          <h1>تقرير المتفرقات</h1>
          <div class="filter-info">الفترة: ${filterText}</div>
          <table>
            <thead>
              <tr>
                <th>التاريخ</th>
                <th>الاسم</th>
                <th>النوع</th>
                <th>المبلغ</th>
                <th>المدفوع</th>
                <th>المتبقي</th>
                <th>الطالب/الصف</th>
              </tr>
            </thead>
            <tbody>
              ${itemsToPrint.map(item => {
                const remaining = Number(item.amount) - (Number(item.total_paid) || 0);
                const studentClass = item.student_name || 
                  item.class_single_name || 
                  (item.classes_names && item.classes_names.length > 0 ? item.classes_names.join(', ') : '-');
                return `
                  <tr>
                    <td>${(() => {
                      const date = new Date(item.date);
                      const day = String(date.getDate()).padStart(2, '0');
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const year = date.getFullYear();
                      return `${day}/${month}/${year}`;
                    })()}</td>
                    <td>${item.name || '-'}</td>
                    <td>${item.type_name || '-'}</td>
                    <td>${Number(item.amount || 0).toFixed(2)} ₪</td>
                    <td>${Number(item.total_paid || 0).toFixed(2)} ₪</td>
                    <td>${remaining.toFixed(2)} ₪</td>
                    <td>${studentClass}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
          <div class="summary">
            <div class="summary-row">
              <span class="summary-label">إجمالي المتفرقات:</span>
              <span>${itemsToPrint.length} عنصر</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">إجمالي المبلغ:</span>
              <span>${Number(totalAmount).toFixed(2)} ₪</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">إجمالي المدفوع:</span>
              <span>${Number(totalPaid).toFixed(2)} ₪</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">إجمالي المتبقي:</span>
              <span>${Number(totalRemaining).toFixed(2)} ₪</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">تاريخ الطباعة:</span>
              <span>${(() => {
                const date = new Date();
                const day = String(date.getDate()).padStart(2, '0');
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const year = date.getFullYear();
                const hours = String(date.getHours()).padStart(2, '0');
                const minutes = String(date.getMinutes()).padStart(2, '0');
                return `${day}/${month}/${year} ${hours}:${minutes}`;
              })()}</span>
            </div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };
  
  const whiteOutlineBtnSx = {
    borderRadius: 2.5,
    fontWeight: 700,
    textTransform: 'none',
    fontFamily: FONT,
    color: '#fff',
    borderColor: 'rgba(255,255,255,0.6)',
    '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.15)' },
  };

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: FONT,
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header Section */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconPackage size={30} color="#fff" />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={800} color="#fff" sx={{ fontFamily: FONT }}>
                متفرقات
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.25, fontFamily: FONT }}
              >
                إدارة وتسجيل ومتابعة المتفرقات المالية
              </Typography>
            </Box>
          </Stack>

          <Stack useFlexGap direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', gap: 1.5 }}>
            <Button
              variant="outlined"
              startIcon={<IconFilter size={18} />}
              onClick={() => setDateFilter(dateFilter === 'all' ? 'today' : 'all')}
              sx={whiteOutlineBtnSx}
            >
              فلترة
            </Button>
            <Button
              variant="outlined"
              startIcon={<IconPrinter size={18} />}
              onClick={handlePrint}
              sx={whiteOutlineBtnSx}
            >
              طباعة
            </Button>
            <Button
              variant="outlined"
              startIcon={<IconPlus size={18} />}
              onClick={() => setTypeDialogOpen(true)}
              sx={whiteOutlineBtnSx}
            >
              إضافة نوع
            </Button>
            <Button
              variant="contained"
              startIcon={<IconPlus size={18} />}
              onClick={() => {
                setSelectedItem(null);
                setItemData({
                  name: '',
                  amount: '',
                  date: new Date().toISOString().split('T')[0],
                  type: '',
                  student: '',
                  class_single: '',
                  classes: [],
                  notes: '',
                });
                setItemDialogOpen(true);
              }}
              sx={{
                bgcolor: '#fff',
                color: '#4338CA',
                px: 3,
                py: 1.1,
                borderRadius: 2.5,
                boxShadow: 'none',
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: FONT,
                '&:hover': { bgcolor: '#EEF2FF', boxShadow: 'none' },
              }}
            >
              إضافة متفرقات
            </Button>
          </Stack>
        </MotionPaper>

        {/* Summary Cards */}
        {summary && (
            <Grid
              container
              spacing={2}
              component={motion.div}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
              sx={{ mb: 3 }}
            >
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    bgcolor: '#fff',
                    boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                    border: '1px solid #E5E7EB',
                    height: '100%',
                  }}
                >
                  <CardContent>
                    <Stack useFlexGap direction="row" alignItems="center" spacing={1.25}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          bgcolor: '#EEF2FF',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <IconPackage size={22} color="#4338CA" />
                      </Box>
                      <Typography sx={{ fontFamily: FONT, fontWeight: 700, color: '#6B7280', fontSize: '0.9rem' }}>
                        إجمالي المتفرقات
                      </Typography>
                    </Stack>
                    <Typography variant="h4" mt={1} sx={{ fontFamily: FONT, fontWeight: 800, color: '#4338CA' }}>
                      {summary.total_items || 0}
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: FONT, color: '#6B7280' }}>
                      {Number(summary.total_amount || 0).toFixed(2)} ₪
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    bgcolor: '#fff',
                    boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                    border: '1px solid #E5E7EB',
                    height: '100%',
                  }}
                >
                  <CardContent>
                    <Stack useFlexGap direction="row" alignItems="center" spacing={1.25}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          bgcolor: '#DCFCE7',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <IconCash size={22} color="#16A34A" />
                      </Box>
                      <Typography sx={{ fontFamily: FONT, fontWeight: 700, color: '#6B7280', fontSize: '0.9rem' }}>
                        المدفوع
                      </Typography>
                    </Stack>
                    <Typography variant="h4" mt={1} sx={{ fontFamily: FONT, fontWeight: 800, color: '#16A34A' }}>
                      {Number(summary.total_paid || 0).toFixed(2)} ₪
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: FONT, color: '#6B7280' }}>
                      من إجمالي {Number(summary.total_amount || 0).toFixed(2)} ₪
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    bgcolor: '#fff',
                    boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                    border: '1px solid #E5E7EB',
                    height: '100%',
                  }}
                >
                  <CardContent>
                    <Stack useFlexGap direction="row" alignItems="center" spacing={1.25}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          bgcolor: '#FEF3C7',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <IconTrendingUp size={22} color="#D97706" />
                      </Box>
                      <Typography sx={{ fontFamily: FONT, fontWeight: 700, color: '#6B7280', fontSize: '0.9rem' }}>
                        المتبقي
                      </Typography>
                    </Stack>
                    <Typography variant="h4" mt={1} sx={{ fontFamily: FONT, fontWeight: 800, color: '#D97706' }}>
                      {Number(summary.remaining_amount || 0).toFixed(2)} ₪
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: FONT, color: '#6B7280' }}>
                      مستحق الدفع
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    bgcolor: '#fff',
                    boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                    border: '1px solid #E5E7EB',
                    height: '100%',
                  }}
                >
                  <CardContent>
                    <Stack useFlexGap direction="row" alignItems="center" spacing={1.25}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          bgcolor: '#E0F2FE',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <IconPackage size={22} color="#0284C7" />
                      </Box>
                      <Typography sx={{ fontFamily: FONT, fontWeight: 700, color: '#6B7280', fontSize: '0.9rem' }}>
                        هذا الشهر
                      </Typography>
                    </Stack>
                    <Typography variant="h4" mt={1} sx={{ fontFamily: FONT, fontWeight: 800, color: '#0284C7' }}>
                      {summary.month_items || 0}
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: FONT, color: '#6B7280' }}>
                      {Number(summary.month_amount || 0).toFixed(2)} ₪
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
          
          {/* Filters Panel */}
          {dateFilter !== 'all' && (
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 3 },
                mb: 3,
                borderRadius: 3,
                bgcolor: '#fff',
                boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
                border: '1px solid #E5E7EB',
              }}
            >
              <Stack useFlexGap direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <IconFilter size={18} color="#4338CA" />
                <Typography sx={{ fontFamily: FONT, fontWeight: 700, color: '#1E1B4B', fontSize: '0.95rem' }}>
                  تصفية النتائج
                </Typography>
              </Stack>
              <Stack useFlexGap direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ gap: 1.5 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>نوع الفلترة</InputLabel>
                  <Select
                    value={dateFilter}
                    label="نوع الفلترة"
                    onChange={(e) => {
                      setDateFilter(e.target.value);
                      if (e.target.value !== 'custom') {
                        setDateFrom('');
                        setDateTo('');
                      }
                    }}
                  >
                    <MenuItem value="all">الكل</MenuItem>
                    <MenuItem value="today">اليوم</MenuItem>
                    <MenuItem value="week">آخر أسبوع</MenuItem>
                    <MenuItem value="custom">مخصص</MenuItem>
                  </Select>
                </FormControl>
                {dateFilter === 'custom' && (
                  <>
                    <TextField
                      size="small"
                      label="من تاريخ"
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ minWidth: 150 }}
                    />
                    <TextField
                      size="small"
                      label="إلى تاريخ"
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ minWidth: 150 }}
                    />
                  </>
                )}
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>النوع</InputLabel>
                  <Select
                    value={typeFilter}
                    label="النوع"
                    onChange={(e) => setTypeFilter(e.target.value)}
                  >
                    <MenuItem value="all">الكل</MenuItem>
                    {types.map(type => (
                      <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>الطالب</InputLabel>
                  <Select
                    value={studentFilter}
                    label="الطالب"
                    onChange={(e) => setStudentFilter(e.target.value)}
                  >
                    <MenuItem value="all">الكل</MenuItem>
                    {students.map(student => (
                      <MenuItem key={student.id} value={student.id}>
                        {student.first_name} {student.second_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>الصف</InputLabel>
                  <Select
                    value={classFilter}
                    label="الصف"
                    onChange={(e) => setClassFilter(e.target.value)}
                  >
                    <MenuItem value="all">الكل</MenuItem>
                    {classes.map(cls => (
                      <MenuItem key={cls.id} value={cls.id}>{cls.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Chip
                  label={`${filteredItems.length} عنصر`}
                  size="small"
                  sx={{
                    bgcolor: '#EEF2FF',
                    color: '#4338CA',
                    fontWeight: 700,
                    fontFamily: FONT,
                  }}
                />
              </Stack>
            </Paper>
          )}

          {/* Summary for filtered data */}
          {filteredItems.length > 0 && dateFilter !== 'all' && (
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 3 },
                mb: 3,
                borderRadius: 3,
                color: '#fff',
                background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" sx={{ fontFamily: FONT, color: 'rgba(255,255,255,0.85)' }}>إجمالي المتفرقات المفلترة</Typography>
                  <Typography variant="h5" sx={{ fontFamily: FONT, fontWeight: 800 }}>
                    {filteredItems.length} عنصر
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" sx={{ fontFamily: FONT, color: 'rgba(255,255,255,0.85)' }}>إجمالي المبلغ</Typography>
                  <Typography variant="h5" sx={{ fontFamily: FONT, fontWeight: 800 }}>
                    {Number(filteredItems.reduce((sum, item) => sum + (Number(item.amount) || 0), 0)).toFixed(2)} ₪
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" sx={{ fontFamily: FONT, color: 'rgba(255,255,255,0.85)' }}>إجمالي المتبقي</Typography>
                  <Typography variant="h5" sx={{ fontFamily: FONT, fontWeight: 800 }}>
                    {Number(filteredItems.reduce((sum, item) => {
                      const remaining = Number(item.amount) - (Number(item.total_paid) || 0);
                      return sum + remaining;
                    }, 0)).toFixed(2)} ₪
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          )}

        {/* Items Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (dateFilter === 'all' ? items : filteredItems).length === 0 ? (
          <Alert
            severity="info"
            sx={{ borderRadius: 3, fontFamily: FONT, boxShadow: '0 2px 12px rgba(67,56,202,0.07)' }}
          >
            لا توجد متفرقات
          </Alert>
        ) : (
          <TableContainer
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
            id="miscellaneous-table"
            sx={{
              borderRadius: 3,
              bgcolor: '#fff',
              boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
              border: '1px solid #E5E7EB',
              overflow: 'hidden',
            }}
          >
            <Table
              sx={{
                fontFamily: FONT,
                '& th, & td': { fontFamily: FONT, borderColor: '#E5E7EB' },
                '& tbody tr:hover': { backgroundColor: '#F8F9FF' },
              }}
            >
              <TableHead>
                <TableRow sx={{ '& th': { bgcolor: '#F1F5F9', fontWeight: 700, color: '#1E1B4B' } }}>
                  <TableCell>التاريخ</TableCell>
                  <TableCell>الاسم</TableCell>
                  <TableCell>النوع</TableCell>
                  <TableCell align="right">المبلغ</TableCell>
                  <TableCell align="right">المدفوع</TableCell>
                  <TableCell align="right">المتبقي</TableCell>
                  <TableCell>الطالب/الصف</TableCell>
                  <TableCell align="center">الحالة</TableCell>
                  <TableCell align="center">إجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(dateFilter === 'all' ? items : filteredItems).map((item) => {
                  const remaining = Number(item.amount) - (Number(item.total_paid) || 0);
                  const studentClass = item.student_name ||
                    item.class_single_name ||
                    (item.classes_names && item.classes_names.length > 0 ? item.classes_names.join(', ') : '-');
                  return (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        {(() => {
                          const date = new Date(item.date);
                          const day = String(date.getDate()).padStart(2, '0');
                          const month = String(date.getMonth() + 1).padStart(2, '0');
                          const year = date.getFullYear();
                          return `${day}/${month}/${year}`;
                        })()}
                      </TableCell>
                      <TableCell>{item.name || '-'}</TableCell>
                      <TableCell>{item.type_name || '-'}</TableCell>
                      <TableCell align="right">{Number(item.amount || 0).toFixed(2)} ₪</TableCell>
                      <TableCell align="right">{Number(item.total_paid || 0).toFixed(2)} ₪</TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          sx={{ fontFamily: FONT, color: remaining > 0 ? '#DC2626' : '#16A34A' }}
                        >
                          {remaining.toFixed(2)} ₪
                        </Typography>
                      </TableCell>
                      <TableCell>{studentClass}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={item.is_fully_paid ? 'مدفوع بالكامل' : 'غير مدفوع'}
                          size="small"
                          sx={{
                            fontFamily: FONT,
                            fontWeight: 700,
                            bgcolor: item.is_fully_paid ? '#DCFCE7' : '#FEF3C7',
                            color: item.is_fully_paid ? '#16A34A' : '#D97706',
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Stack useFlexGap direction="row" spacing={0.5} justifyContent="center">
                          {remaining > 0 && (
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedItem(item);
                                setPaymentData({
                                  amount: remaining.toString(),
                                  payment_date: new Date().toISOString().split('T')[0],
                                  payment_method: 'cash',
                                  cheque_number: '',
                                  cheque_date: '',
                                  notes: '',
                                });
                                setPaymentDialogOpen(true);
                              }}
                              title="دفع"
                              sx={{ color: '#16A34A', '&:hover': { bgcolor: '#DCFCE7' } }}
                            >
                              <IconCash size={18} />
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedItem(item);
                              setItemData({
                                name: item.name,
                                amount: item.amount.toString(),
                                date: item.date,
                                type: item.type || '',
                                student: item.student || '',
                                class_single: item.class_single || '',
                                classes: item.classes || [],
                                notes: item.notes || '',
                              });
                              setItemDialogOpen(true);
                            }}
                            title="تعديل"
                            sx={{ color: '#D97706', '&:hover': { bgcolor: '#FEF3C7' } }}
                          >
                            <IconEdit size={18} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteItem(item)}
                            title="حذف"
                            sx={{ color: '#DC2626', '&:hover': { bgcolor: '#FEE2E2' } }}
                          >
                            <IconTrash size={18} />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        
        {/* Add/Edit Item Dialog */}
        <Dialog
          open={itemDialogOpen}
          onClose={() => {
            setItemDialogOpen(false);
            setSelectedItem(null);
          }}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, fontFamily: FONT } }}
        >
          <DialogTitle
            sx={{ fontFamily: FONT, fontWeight: 800, color: '#1E1B4B', borderBottom: '1px solid #E5E7EB' }}
          >
            {selectedItem ? 'تعديل متفرقات' : 'إضافة متفرقات جديدة'}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="اسم العنصر"
                value={itemData.name}
                onChange={(e) => setItemData({ ...itemData, name: e.target.value })}
                required
                placeholder="مثال: رحلة إلى القدس"
              />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="المبلغ"
                    type="number"
                    value={itemData.amount}
                    onChange={(e) => setItemData({ ...itemData, amount: e.target.value })}
                    inputProps={{ min: 0, step: 0.01 }}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="التاريخ"
                    type="date"
                    value={itemData.date}
                    onChange={(e) => setItemData({ ...itemData, date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                </Grid>
              </Grid>
              <FormControl fullWidth>
                <InputLabel>النوع (اختياري)</InputLabel>
                <Select
                  value={itemData.type}
                  label="النوع (اختياري)"
                  onChange={(e) => setItemData({ ...itemData, type: e.target.value })}
                >
                  <MenuItem value="">لا يوجد</MenuItem>
                  {types.map(type => (
                    <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Alert severity="info">
                يمكنك ربط العنصر بطالب محدد أو صف محدد أو عدة صفوف (اختياري)
              </Alert>
              <FormControl fullWidth>
                <InputLabel>طالب محدد (اختياري)</InputLabel>
                <Select
                  value={itemData.student}
                  label="طالب محدد (اختياري)"
                  onChange={(e) => setItemData({ ...itemData, student: e.target.value, class_single: '', classes: [] })}
                >
                  <MenuItem value="">لا يوجد</MenuItem>
                  {students.map(student => (
                    <MenuItem key={student.id} value={student.id}>
                      {student.first_name} {student.second_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>صف محدد (اختياري)</InputLabel>
                <Select
                  value={itemData.class_single}
                  label="صف محدد (اختياري)"
                  onChange={(e) => setItemData({ ...itemData, class_single: e.target.value, student: '', classes: [] })}
                >
                  <MenuItem value="">لا يوجد</MenuItem>
                  {classes.map(cls => (
                    <MenuItem key={cls.id} value={cls.id}>{cls.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>عدة صفوف (اختياري)</InputLabel>
                <Select
                  multiple
                  value={itemData.classes}
                  label="عدة صفوف (اختياري)"
                  onChange={(e) => setItemData({ ...itemData, classes: e.target.value, student: '', class_single: '' })}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const cls = classes.find(c => c.id === value);
                        return <Chip key={value} label={cls?.name || value} size="small" />;
                      })}
                    </Box>
                  )}
                >
                  {classes.map(cls => (
                    <MenuItem key={cls.id} value={cls.id}>
                      <Checkbox checked={itemData.classes.indexOf(cls.id) > -1} />
                      {cls.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="ملاحظات"
                multiline
                rows={3}
                value={itemData.notes}
                onChange={(e) => setItemData({ ...itemData, notes: e.target.value })}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid #E5E7EB' }}>
            <Button
              variant="outlined"
              onClick={() => {
                setItemDialogOpen(false);
                setSelectedItem(null);
              }}
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: FONT,
                borderColor: '#E5E7EB',
                color: '#6B7280',
                '&:hover': { borderColor: '#6B7280', bgcolor: '#F8F9FF' },
              }}
            >
              إلغاء
            </Button>
            <Button
              onClick={handleSaveItem}
              variant="contained"
              disabled={saving || !itemData.name || !itemData.amount}
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: FONT,
                bgcolor: '#4338CA',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
              }}
            >
              {saving ? 'جاري الحفظ...' : 'حفظ'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Type Dialog */}
        <Dialog
          open={typeDialogOpen}
          onClose={() => setTypeDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, fontFamily: FONT } }}
        >
          <DialogTitle
            sx={{ fontFamily: FONT, fontWeight: 800, color: '#1E1B4B', borderBottom: '1px solid #E5E7EB' }}
          >
            إضافة نوع جديد
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="اسم النوع"
                value={typeData.name}
                onChange={(e) => setTypeData({ ...typeData, name: e.target.value })}
                required
                placeholder="مثال: رحلة، هدية، نشاط"
              />
              <TextField
                fullWidth
                label="الوصف (اختياري)"
                multiline
                rows={3}
                value={typeData.description}
                onChange={(e) => setTypeData({ ...typeData, description: e.target.value })}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid #E5E7EB' }}>
            <Button
              variant="outlined"
              onClick={() => setTypeDialogOpen(false)}
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: FONT,
                borderColor: '#E5E7EB',
                color: '#6B7280',
                '&:hover': { borderColor: '#6B7280', bgcolor: '#F8F9FF' },
              }}
            >
              إلغاء
            </Button>
            <Button
              onClick={handleSaveType}
              variant="contained"
              disabled={!typeData.name.trim()}
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: FONT,
                bgcolor: '#4338CA',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
              }}
            >
              حفظ
            </Button>
          </DialogActions>
        </Dialog>

        {/* Payment Dialog */}
        <Dialog
          open={paymentDialogOpen}
          onClose={() => {
            setPaymentDialogOpen(false);
            setSelectedItem(null);
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, fontFamily: FONT } }}
        >
          <DialogTitle
            sx={{ fontFamily: FONT, fontWeight: 800, color: '#1E1B4B', borderBottom: '1px solid #E5E7EB' }}
          >
            تسجيل دفعة
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 2 }}>
              {selectedItem && (
                <Alert severity="info">
                  العنصر: {selectedItem.name} - المتبقي: {Number(selectedItem.amount) - (Number(selectedItem.total_paid) || 0)} ₪
                </Alert>
              )}
              <TextField
                fullWidth
                label="المبلغ"
                type="number"
                value={paymentData.amount}
                onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                inputProps={{ min: 0, step: 0.01 }}
                required
              />
              <TextField
                fullWidth
                label="تاريخ الدفع"
                type="date"
                value={paymentData.payment_date}
                onChange={(e) => setPaymentData({ ...paymentData, payment_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
              <FormControl fullWidth>
                <InputLabel>طريقة الدفع</InputLabel>
                <Select
                  value={paymentData.payment_method}
                  label="طريقة الدفع"
                  onChange={(e) => setPaymentData({ ...paymentData, payment_method: e.target.value })}
                >
                  <MenuItem value="cash">نقدي</MenuItem>
                  <MenuItem value="cheque">شيك</MenuItem>
                  <MenuItem value="visa">فيزا</MenuItem>
                  <MenuItem value="bank_transfer">تحويل بنكي</MenuItem>
                  <MenuItem value="other">أخرى</MenuItem>
                </Select>
              </FormControl>
              {paymentData.payment_method === 'cheque' && (
                <>
                  <TextField
                    fullWidth
                    label="رقم الشيك"
                    value={paymentData.cheque_number}
                    onChange={(e) => setPaymentData({ ...paymentData, cheque_number: e.target.value })}
                  />
                  <TextField
                    fullWidth
                    label="تاريخ الشيك"
                    type="date"
                    value={paymentData.cheque_date}
                    onChange={(e) => setPaymentData({ ...paymentData, cheque_date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </>
              )}
              <TextField
                fullWidth
                label="ملاحظات"
                multiline
                rows={3}
                value={paymentData.notes}
                onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid #E5E7EB' }}>
            <Button
              variant="outlined"
              onClick={() => {
                setPaymentDialogOpen(false);
                setSelectedItem(null);
              }}
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: FONT,
                borderColor: '#E5E7EB',
                color: '#6B7280',
                '&:hover': { borderColor: '#6B7280', bgcolor: '#F8F9FF' },
              }}
            >
              إلغاء
            </Button>
            <Button
              onClick={handleSavePayment}
              variant="contained"
              disabled={saving || !paymentData.amount}
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                fontFamily: FONT,
                bgcolor: '#4338CA',
                boxShadow: 'none',
                '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
              }}
            >
              {saving ? 'جاري الحفظ...' : 'حفظ'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default MiscellaneousPage;
